import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Instructions } from '../../content/instructions';
import { DataContentProps } from '../../newron-core';
import { DecompositionChart } from '../../design/charts/decompositionChart';
import { Loading } from '../../design/loading/Loading';
import { ContributionKpiConfig, KpiDecompositionInfo } from './types';
import { makeDecompositionInfo } from './data';
import { DataWarning } from '../../types';
import GenericWarnings from '../../design/warning/GenericWarnings';
import { getExportContainer } from '../../export';
import { useCorePageState } from '../../page/provider';
import { useOnCollectionExportReady } from '../collection/useOnCollectionExportReady';
import { EmptyState } from '../../common/no-data-warning/empty-state';
import { useMobile } from '../../context/mobile/mobile.provider';
import { useMobileChart } from '../mobileChart/mobileChart.hook';
import classNames from 'classnames';

function KpiDecomposition({
  data,
  isLoading,
  instructions = {},
  componentSettings,
  hasContentHaveData,
  componentName,
  collection,
  componentHeadingTitle,
}: DataContentProps) {
  const [dataStamp, setDataStamp] = useState<number>();
  const [info, setInfo] = useState<KpiDecompositionInfo | undefined>();
  const [kpiLabel, setKpiLabel] = useState<string>();
  const [warnings, setWarnings] = useState<DataWarning[]>([]);
  const { dsl, globalWarnings } = useCorePageState();
  const exportRef = useRef<HTMLDivElement[]>([]);

  const allColumns = data?.getAllColumns() ?? [];

  const { isMobile } = useMobile();
  const { chartFullscreen, setChartFullscreen, MobileChartButton } = useMobileChart();

  useOnCollectionExportReady({
    collection,
    dsl,
    chartSliceRefs: exportRef,
    componentName,
    componentSettings,
    allColumns,
    componentHeadingTitle,
  });

  const myInstructions = new Instructions(instructions, componentSettings);
  const decomposedKpi = myInstructions.getString('decomposedKpi');
  const periodName = myInstructions.getString('periodName');
  const contributionKpis = myInstructions.getObject('contributingKpis') as ContributionKpiConfig[];

  const updateData = () => {
    if (!data) {
      return;
    }

    setDataStamp(data.stamp);

    const kpis = [decomposedKpi, ...(contributionKpis?.map(({ kpi }) => kpi) || [])];
    const dataWarnings = data.getDataWarnings(kpis);
    if (dataWarnings?.length) {
      setWarnings(dataWarnings);
      hasContentHaveData && hasContentHaveData('isWarning');
      return;
    }

    if (warnings.length) {
      setWarnings([]);
    }
    const kpiColumn = data.getDataColumn(decomposedKpi);
    setKpiLabel(kpiColumn?.label);

    const info = makeDecompositionInfo(data, { decomposedKpi, contributionKpis });
    setInfo(info);
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!data || data.stamp === dataStamp) {
      return;
    }
    updateData();
  }, [data]);

  useEffect(() => {
    if (data) {
      collection?.onLoaded();
    }
  }, [data]);

  const ExportContainer = useMemo(() => getExportContainer(componentName), []);
  const shouldShowWarning = warnings.length !== 0 || info?.isKpiQualityOk === false;

  if (collection && !info && !info?.info?.length) {
    collection.onExportReady(null);
    return <EmptyState />;
  }

  return (
    <div className={classNames({ 'chart-full-screen': chartFullscreen })}>
      {isMobile && !isLoading && !shouldShowWarning && (
        <MobileChartButton chartFullscreen={chartFullscreen} setChartFullscreen={setChartFullscreen} />
      )}
      <div
        className={classNames('kpi-decomposition bg-white rounded-2 p-4 ', {
          'pt-7': isMobile,
        })}
        style={{ minWidth: '620px', maxWidth: '1100px' }}
      >
        <Loading isActive={isLoading} withIndicator />

        {shouldShowWarning ? (
          <GenericWarnings
            className=""
            warnings={[
              {
                level: 'warn',
                message:
                  'Revenue decomposition is not available for your selected market or channel, try modifying your selections',
              },
            ]}
          />
        ) : info ? (
          <>
            <DecompositionChart info={info.info} chartFullscreen={chartFullscreen} />
            <p className={classNames({ 'width-90': isMobile })}>
              The combined impact of changes above caused {info.kpiChange > 0 ? 'an' : 'a'}
              <span className="font-bold">
                {` ${info.kpiChange > 0 ? 'increase' : 'decrease'} in total ${kpiLabel.toLowerCase()} of ${
                  info.kpiChangeFormatted
                } in ${periodName || `${info.labels[1]} compared with ${info.labels[0]}`}`}
              </span>
              .
            </p>
          </>
        ) : null}
        <ExportContainer>
          <div className={`${componentName}-export-slide `} ref={el => (exportRef.current = [el])}>
            {shouldShowWarning ? (
              <GenericWarnings
                className=""
                warnings={[
                  {
                    level: 'warn',
                    message:
                      'Revenue decomposition is not available for your selected market or channel, try modifying your selections',
                  },
                ]}
              />
            ) : info ? (
              <>
                {globalWarnings && <GenericWarnings warnings={globalWarnings} />}
                <DecompositionContent info={info} kpiLabel={kpiLabel} periodName={periodName} />
              </>
            ) : null}
          </div>
        </ExportContainer>
      </div>
    </div>
  );
}

const DecompositionContent = ({
  info,
  kpiLabel,
  periodName,
}: {
  info: KpiDecompositionInfo;
  kpiLabel?: string;
  periodName?: string;
}) => {
  const changeType = info.kpiChange > 0 ? 'increase' : 'decrease';
  const comparisonPeriod = periodName ?? `${info.labels[1]} compared with ${info.labels[0]}`;

  return (
    <>
      <DecompositionChart info={info.info} />
      <p>
        The combined impact of changes above caused {info.kpiChange > 0 ? 'an' : 'a'}
        <span className="font-bold">
          {` ${changeType} in total ${kpiLabel?.toLowerCase()} of ${info.kpiChangeFormatted} in ${comparisonPeriod}`}
        </span>
        .
      </p>
    </>
  );
};

export { KpiDecomposition };
